import React, { useState } from 'react';
import { Button, Text, Stack } from '@solvhealth/jigsaw-v1';
import { AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { analyticsTrackEvent } from '../../core/analytics';

import dataTestIds from '~/test/test-ids';
import {
  everydayHealthLinks,
  urgentCareLinks,
  womensHealthLinks,
  popularTestLinks,
  LinkCategoryList,
  Link,
} from './links';

import {
  HeaderContainer,
  LogoContainer,
  Nav,
  NavLinks,
  StyledLinkText,
  NavLink,
  LinkContainer,
  LinkContainerWrap,
  StyledList,
  SearchTriangle,
  FindCareLink,
  CrunchBerryHover,
  LinkGridContainer,
  StyledAccordion,
  StyledAccordionSummary,
  ShowDesktop,
} from './styles';
import {
  HEADER_DIRECTORY_LINKS_CLICK_LOGO,
  HEADER_DIRECTORY_LINKS_HIDE_DESKTOP_LINK_MENU,
  HEADER_DIRECTORY_LINKS_LINK_CLICK,
  HEADER_DIRECTORY_LINKS_SHOW_DESKTOP_LINK_MENU,
} from '~/core/analytics/events';
import { remixUrl } from '~/core/remix';

const womensHealthCategory = "Women's Health";
const popularTestCategory = 'Popular Tests';
const forProvidersHeader = 'For Providers';
const findCareNearMeText = 'Find Care Near Me';

type DesktopHeaderProps = {
  openLogInModal: () => void;
};

const LinkCategoryAccordions = ({
  category,
  categoryLinks,
  expandedAccordion,
  handleAccordionChange,
}: {
  category: string;
  categoryLinks: LinkCategoryList;
  expandedAccordion: string;
  handleAccordionChange: (panel: string) => (event: any, newExpanded: any) => void;
}) => (
  <div>
    <Text fontSize="sm" fontWeight="bold" textColor="crunchberry">
      {category}
    </Text>
    {categoryLinks.map(({ label: subcategory, links }) => (
      <StyledAccordion
        expanded={expandedAccordion === subcategory}
        id={subcategory}
        key={subcategory}
        onChange={handleAccordionChange(subcategory)}
        title={subcategory}
      >
        <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Text fontSize="sm" fontWeight="bold" textColor="gray-500">
            {subcategory}
          </Text>
        </StyledAccordionSummary>

        <AccordionDetails>
          <StyledList>
            {links.map((link: Link) =>
              !link.mobileOnly ? (
                <li key={link.url}>
                  <a
                    href={link.url}
                    onClick={() =>
                      analyticsTrackEvent(HEADER_DIRECTORY_LINKS_LINK_CLICK, {
                        linkUrl: link.url,
                        linkCategory: category,
                        linkSubcategory: subcategory,
                        label: link.label,
                      })
                    }
                  >
                    <CrunchBerryHover>{link.label}</CrunchBerryHover>
                  </a>
                </li>
              ) : null
            )}
          </StyledList>
        </AccordionDetails>
      </StyledAccordion>
    ))}
  </div>
);

const DesktopHeader = ({ openLogInModal }: DesktopHeaderProps) => {
  const [showLinks, setShowLinks] = useState(false);
  const [expandedAccordion, setExpanded] = useState('');
  const handleAccordionChange =
    (panel: string) => (event: React.ChangeEvent<{}>, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : '');
    };
  return (
    <ShowDesktop>
      <HeaderContainer>
        <LogoContainer
          onClick={() => {
            analyticsTrackEvent(HEADER_DIRECTORY_LINKS_CLICK_LOGO);
          }}
        />
        <Nav>
          <NavLinks>
            <NavLink
              forceBorder={showLinks}
              onMouseEnter={() => {
                setShowLinks(true);
                analyticsTrackEvent(HEADER_DIRECTORY_LINKS_SHOW_DESKTOP_LINK_MENU);
              }}
              onMouseLeave={() => {
                setShowLinks(false);
                analyticsTrackEvent(HEADER_DIRECTORY_LINKS_HIDE_DESKTOP_LINK_MENU);
              }}
            >
              <StyledLinkText color="gray-500" forceActive={showLinks}>
                Browse
              </StyledLinkText>
            </NavLink>
            <NavLink
              href="/for-providers"
              onClick={() =>
                analyticsTrackEvent(HEADER_DIRECTORY_LINKS_LINK_CLICK, {
                  linkUrl: '/for-providers',
                  label: forProvidersHeader,
                })
              }
            >
              <StyledLinkText color="gray-500">{forProvidersHeader}</StyledLinkText>
            </NavLink>
          </NavLinks>
          <Button data-testid={dataTestIds.login.btn} onClick={openLogInModal} variant="outlined">
            Log in / Sign up
          </Button>
        </Nav>
      </HeaderContainer>
      <LinkContainer
        accordionExpanded={!!expandedAccordion}
        onMouseEnter={() => {
          setShowLinks(true);
          analyticsTrackEvent(HEADER_DIRECTORY_LINKS_SHOW_DESKTOP_LINK_MENU);
        }}
        onMouseLeave={() => {
          setShowLinks(false);
          analyticsTrackEvent(HEADER_DIRECTORY_LINKS_HIDE_DESKTOP_LINK_MENU);
        }}
        showLinks={showLinks}
      >
        <LinkContainerWrap>
          <Stack alignItems="center" paddingBottom="32px" row></Stack>

          <LinkGridContainer>
            <Text
              fontSize="lg"
              fontWeight="bold"
              gridColumn="1 / span 3"
              gridRow="1"
              textColor="gray-500"
            >
              Browse Services
            </Text>
            <FindCareLink
              href={remixUrl('/r/search')}
              onClick={() => {
                analyticsTrackEvent(HEADER_DIRECTORY_LINKS_LINK_CLICK, {
                  label: findCareNearMeText,
                  linkUrl: '/r/search',
                });
              }}
            >
              <SearchTriangle src="/images/icons/location_triangle.svg" />
              {findCareNearMeText}
            </FindCareLink>

            <LinkCategoryAccordions
              category="URGENT CARE"
              categoryLinks={urgentCareLinks}
              expandedAccordion={expandedAccordion}
              handleAccordionChange={handleAccordionChange}
            />
            <LinkCategoryAccordions
              category="EVERYDAY HEALTH"
              categoryLinks={everydayHealthLinks}
              expandedAccordion={expandedAccordion}
              handleAccordionChange={handleAccordionChange}
            />
            <div>
              <Text fontSize="sm" fontWeight="bold" textColor="crunchberry">
                {womensHealthCategory.toUpperCase()}
              </Text>
              {womensHealthLinks.map((link) => (
                <a
                  href={link.url}
                  key={link.label}
                  onClick={() =>
                    analyticsTrackEvent(HEADER_DIRECTORY_LINKS_LINK_CLICK, {
                      linkUrl: link.url,
                      label: link.label,
                      linkCategory: womensHealthCategory,
                    })
                  }
                >
                  <CrunchBerryHover paddingTop="12px">{link.label}</CrunchBerryHover>
                </a>
              ))}
            </div>
            <div>
              <Text fontSize="sm" fontWeight="bold" textColor="crunchberry">
                {popularTestCategory.toUpperCase()}
              </Text>
              {popularTestLinks.map((link) => (
                <a
                  href={link.url}
                  key={link.label}
                  onClick={() =>
                    analyticsTrackEvent(HEADER_DIRECTORY_LINKS_LINK_CLICK, {
                      label: link.label,
                      linkUrl: link.url,
                      linkCategory: popularTestCategory,
                    })
                  }
                >
                  <CrunchBerryHover paddingTop="12px">{link.label}</CrunchBerryHover>
                </a>
              ))}
            </div>
          </LinkGridContainer>
        </LinkContainerWrap>
      </LinkContainer>
    </ShowDesktop>
  );
};

export default DesktopHeader;
